import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Image, Platform, StyleSheet, Text, View } from 'react-native';
import Toast from 'react-native-toast-message';
import { useDispatch, useSelector } from 'react-redux';

import { BgPortraitPng } from '../../assets';
import { deltaDNAService } from '../../services/deltaDNA/deltadna.service';
import {
	achievementShowcaseErrorDismiss,
	achievementShowcaseToggle,
	achievementsHubRequest,
} from '../../store/achievements/achievementsActions';
import {
	selectAchievementFamilies,
	selectAchievementsHubErrorKey,
	selectAchievementsLoaded,
	selectAchievementsMaxFeatured,
	selectFeaturedFamilyKeys,
	selectShowcaseSaveErrorKey,
} from '../../store/achievements/achievementsReducer';
import { useDevice } from '../../utils/hooks/useDevice';
import { useSafeViewport } from '../../utils/hooks/useSafeViewport';
import AchievementCard, { CARD_WIDTH } from '../Components/Achievements/AchievementCard';
import AchievementDetails, {
	DETAILS_NO_ACTION_TRIM,
} from '../Components/Achievements/AchievementDetails';
import AchievementFilters, {
	AchievementFilterKey,
} from '../Components/Achievements/AchievementFilters';
import AchievementsEmptyState from '../Components/Achievements/AchievementsEmptyState';
import AchievementsHeader from '../Components/Achievements/AchievementsHeader';
import {
	ACHIEVEMENTS_FONT,
	achievementsColors,
	achievementsGlow,
	DETAILS_PANEL_HEIGHT,
	PANEL_HEIGHT,
	PANEL_WIDTH,
	PANEL_Y,
	RADIUS_PANEL,
} from '../Components/Achievements/achievementsTheme';
import {
	AchievementCategoryOption,
	achievementText,
	presentCategories,
	toAchievementViews,
} from '../Components/Achievements/achievementsViewModel';
import CategoriesDropdown, { DROPDOWN_WIDTH } from '../Components/Achievements/CategoriesDropdown';
import MaxSelectedDialog from '../Components/Achievements/MaxSelectedDialog';
import SelectedForProfile from '../Components/Achievements/SelectedForProfile';
import { IndicatorScrollView, SCROLL_INDICATOR } from '../Components/common/ScrollIndicator';
import { Pressable } from '../Components/common/Touchable';
import DesktopPanelFill from '../Components/Desktop/DesktopPanelFill';
import { useRedesignScale } from '../Components/Modals/useRedesignScale';
import { PROFILE_SCRIM } from '../Components/Profile/ProfileRedesignEditView';
import { RootStackParamList } from '../navigator';

/**
 * The modal behind the side menu's Achievements entry - Figma section "01 · Entry point"
 * (4488:19598) on page "PAVEL DRAFT". The section storyboards one flow across five artboards:
 * the grid with an empty profile shelf, the same grid once something is on the shelf, and the
 * detail modal behind a card in both its add and remove states. All of them are the same 358-wide
 * panel over a scrim, so this screen is one route that swaps its body, registered as a
 * transparent modal the way the inventory is.
 */
type Nav = NativeStackNavigationProp<RootStackParamList>;

// "Content" (4488:19629) starts under the 56-tall header and insets 12 a side.
const CONTENT_X = 12;
const SUBTITLE = { top: 0, height: 28 };
const FILTERS_TOP = 40;
const GRID = { top: 86, width: 334, height: 394 };
const SHELF_TOP = 492;
/** The menu hangs under the Categories chip, flush with the filter row's right edge. */
const MENU = { top: 78, right: 334 };
const GRID_GAP = 8;
/** Room left under the panel when it has to fit the viewport. */
const PANEL_BOTTOM_MARGIN = 16;
const COLUMNS = 3;

const AchievementsScreen = () => {
	const { goBack } = useNavigation<Nav>();
	const { width, height, insets } = useSafeViewport();
	const { isDesktop } = useDevice();
	const { t, i18n } = useTranslation();
	const dispatch = useDispatch();

	const families = useSelector(selectAchievementFamilies);
	const loaded = useSelector(selectAchievementsLoaded);
	const hubErrorKey = useSelector(selectAchievementsHubErrorKey);
	const maxSelected = useSelector(selectAchievementsMaxFeatured);
	const selectedKeys = useSelector(selectFeaturedFamilyKeys);
	const saveErrorKey = useSelector(selectShowcaseSaveErrorKey);

	const [filter, setFilter] = useState<AchievementFilterKey>('all');
	const [category, setCategory] = useState<AchievementCategoryOption | undefined>(undefined);
	const [menuOpen, setMenuOpen] = useState(false);
	const [openKey, setOpenKey] = useState<string | undefined>(undefined);
	const [maxDialogOpen, setMaxDialogOpen] = useState(false);

	useEffect(() => {
		dispatch(achievementsHubRequest());
	}, [dispatch]);

	const errorKeyBeforeOpen = useRef(saveErrorKey);

	useEffect(() => {
		if (!saveErrorKey) return;

		if (saveErrorKey !== errorKeyBeforeOpen.current) {
			Toast.show({ type: 'error', text1: achievementText(t, saveErrorKey) });
		}
		errorKeyBeforeOpen.current = undefined;
		dispatch(achievementShowcaseErrorDismiss());
	}, [saveErrorKey, dispatch, t]);

	// The panel keeps the design's proportions and never outgrows the viewport it sits in.
	const scale = useRedesignScale({
		// Laid out absolutely against a fixed-height panel, so the whole of it has to fit.
		// On desktop the panel is centred rather than hung 44 from the top.
		fitHeight: isDesktop
			? Math.max(PANEL_HEIGHT, DETAILS_PANEL_HEIGHT)
			: PANEL_Y + Math.max(PANEL_HEIGHT, DETAILS_PANEL_HEIGHT) + PANEL_BOTTOM_MARGIN,
	});
	const styles = useMemo(() => createStyles(scale), [scale]);

	const achievements = useMemo(
		() => toAchievementViews(families, selectedKeys),
		[families, selectedKeys],
	);
	const categories = useMemo(() => presentCategories(achievements), [achievements]);

	const open = achievements.find(achievement => achievement.familyKey === openKey);
	const openRef = useRef(open);
	openRef.current = open;

	useEffect(() => {
		const opened = openRef.current;
		if (!opened) return;

		deltaDNAService.achievementViewedEvent(opened.shownTier.key, Platform.OS, i18n.language);
	}, [openKey, i18n]);

	const visible = achievements.filter(achievement => {
		const matchesState =
			filter === 'all' ? true : filter === 'unlocked' ? !achievement.locked : achievement.locked;

		return matchesState && (category === undefined || achievement.category === category.category);
	});
	// Five slots, filled left to right in the order they were chosen.
	const shelf = Array.from({ length: maxSelected }, (_slot, index) =>
		achievements.find(achievement => achievement.familyKey === selectedKeys[index]),
	);

	const shownSelectedCount = selectedKeys.filter(key =>
		achievements.some(achievement => achievement.familyKey === key && !achievement.locked),
	).length;

	const toggleSelected = (familyKey: string) => {
		if (!selectedKeys.includes(familyKey) && shownSelectedCount >= maxSelected) {
			setMaxDialogOpen(true);
			return;
		}

		dispatch(achievementShowcaseToggle({ familyKey }));
	};

	const panelHeight = open
		? DETAILS_PANEL_HEIGHT - (open.locked ? DETAILS_NO_ACTION_TRIM : 0)
		: PANEL_HEIGHT;
	// The desktop frame centres the dialog; the phone frame hangs it where the artboard does, but
	// never under a notch or Dynamic Island that sits lower than the artboard's status bar.
	const panelTop = isDesktop
		? (height - panelHeight * scale) / 2
		: Math.max(PANEL_Y * scale, insets.top);
	// `root` centres the panel, so on a screen wider than the artboard it is not at PANEL_X.
	const panelLeft = (width - PANEL_WIDTH * scale) / 2;

	return (
		<View style={styles.root}>
			{/* "Backdrop / Home / Mobile" (4488:19602) is the Home screen, nav and all, under the
			    scrim: the live screen behind shows through (MainScreens keeps its body for this). */}
			<View pointerEvents='none' style={styles.scrim} />

			{/* Tapping outside closes the modal, the way every other overlay in the app does. */}
			<Pressable accessibilityRole='button' style={StyleSheet.absoluteFill} onPress={goBack} />

			<View style={[styles.panel, { height: panelHeight * scale, top: panelTop }]}>
				{/* The phone modal carries no fill of its own: its interior is the app's red texture,
				    lined up with the screen around it. The desktop panels paint their own vignette. */}
				{isDesktop ? (
					<DesktopPanelFill radius={RADIUS_PANEL * scale} scale={scale} />
				) : (
					<View pointerEvents='none' style={styles.backdropClip}>
						<Image
							source={BgPortraitPng}
							style={[styles.backdrop, { width, height, left: -panelLeft, top: -panelTop }]}
							resizeMode='cover'
						/>
					</View>
				)}

				<AchievementsHeader
					title={achievementText(
						t,
						open ? 'T_SITE_ACHIEVEMENTS_DETAILS_TITLE' : 'T_SITE_ACHIEVEMENTS_TITLE',
					)}
					// From the detail modal the back arrow returns to the grid it was opened from.
					onBack={open ? () => setOpenKey(undefined) : goBack}
					onClose={goBack}
					scale={scale}
				/>

				{open ? (
					<AchievementDetails
						achievement={open}
						selected={selectedKeys.includes(open.familyKey)}
						onToggleProfile={() => toggleSelected(open.familyKey)}
						scale={scale}
					/>
				) : (
					<View style={styles.content}>
						<Text numberOfLines={2} style={styles.subtitle}>
							{achievementText(t, 'T_SITE_ACHIEVEMENTS_SUBTITLE')}
						</Text>

						<View style={styles.filters}>
							<AchievementFilters
								value={filter}
								onChange={next => {
									setMenuOpen(false);
									setFilter(next);
								}}
								category={category}
								menuOpen={menuOpen}
								onToggleMenu={() => setMenuOpen(isOpen => !isOpen)}
								onClearCategory={() => {
									setCategory(undefined);
									setMenuOpen(false);
								}}
								scale={scale}
							/>
						</View>

						{/* "Achievements / Grid" (4488:19636) draws three rows and clips; the list is
						    longer than the nine cards it shows, so it scrolls here. */}
						{!loaded ? (
							<View style={[styles.grid, styles.status]}>
								{hubErrorKey ? (
									<>
										<Text style={styles.statusText}>{achievementText(t, hubErrorKey)}</Text>
										<Pressable
											accessibilityRole='button'
											onPress={() => dispatch(achievementsHubRequest())}
											style={styles.retry}
										>
											<Text style={styles.retryLabel}>
												{achievementText(t, 'T_SITE_ACHIEVEMENTS_RETRY')}
											</Text>
										</Pressable>
									</>
								) : (
									<ActivityIndicator color={achievementsColors.gold50} />
								)}
							</View>
						) : visible.length === 0 ? (
							<View style={styles.grid}>
								<AchievementsEmptyState scale={scale} />
							</View>
						) : (
							// The three columns fill the grid's 334, so the bar sits in the 12 between the
							// grid and the rim instead of taking a gutter out of the grid.
							<IndicatorScrollView
								style={styles.grid}
								contentContainerStyle={styles.gridContent}
								showsVerticalScrollIndicator={false}
								reserveGutter={false}
								indicatorInsets={{
									right: -(CONTENT_X / 2) * scale - SCROLL_INDICATOR.width / 2,
								}}
							>
								{visible.map(achievement => (
									<AchievementCard
										key={achievement.familyKey}
										achievement={achievement}
										selected={selectedKeys.includes(achievement.familyKey)}
										onPress={() => setOpenKey(achievement.familyKey)}
										onAdd={() => toggleSelected(achievement.familyKey)}
										scale={scale}
									/>
								))}
							</IndicatorScrollView>
						)}

						{/* Last, so it paints over the grid it covers; tapping anywhere else closes it. */}
						{menuOpen && (
							<>
								<Pressable
									accessibilityRole='button'
									style={StyleSheet.absoluteFill}
									onPress={() => setMenuOpen(false)}
								/>
								<View style={styles.menu}>
									<CategoriesDropdown
										categories={categories}
										value={category}
										onChange={next => {
											setCategory(next);
											setMenuOpen(false);
										}}
										scale={scale}
									/>
								</View>
							</>
						)}

						<View style={styles.shelf}>
							<SelectedForProfile
								selected={shelf}
								maxSelected={maxSelected}
								onOpen={achievement => setOpenKey(achievement.familyKey)}
								onRemove={achievement => toggleSelected(achievement.familyKey)}
								scale={scale}
							/>
						</View>
					</View>
				)}
			</View>

			{maxDialogOpen && <MaxSelectedDialog onClose={() => setMaxDialogOpen(false)} scale={scale} />}
		</View>
	);
};

const createStyles = (scale: number) =>
	StyleSheet.create({
		root: {
			flex: 1,
			alignItems: 'center',
		},
		// The backdrop's own "Overlay" over the Home screen - the darker scrim the Inventory and
		// Profile tabs use, not the lighter one popups get over a repainted backdrop.
		scrim: {
			...StyleSheet.absoluteFillObject,
			backgroundColor: PROFILE_SCRIM,
		},
		panel: {
			position: 'absolute',
			top: PANEL_Y * scale,
			width: PANEL_WIDTH * scale,
			borderRadius: RADIUS_PANEL * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.panelBorder,
			overflow: 'hidden',
			...achievementsGlow.panel,
		},
		// The panel's own `overflow: hidden` does not reliably clip this oversized child to the
		// rounded corners once the panel also carries a shadow, so it gets its own rounded clip.
		backdropClip: {
			...StyleSheet.absoluteFillObject,
			borderRadius: RADIUS_PANEL * scale,
			overflow: 'hidden',
		},
		backdrop: {
			position: 'absolute',
		},
		content: {
			flex: 1,
		},
		// Figma "Body/Pill": Sofia Sans Regular 14, line-height 14, gold/50.
		subtitle: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: SUBTITLE.top * scale,
			width: GRID.width * scale,
			height: SUBTITLE.height * scale,
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '400',
			lineHeight: 14 * scale,
			textAlign: 'center',
		},
		filters: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: FILTERS_TOP * scale,
		},
		grid: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: GRID.top * scale,
			width: GRID.width * scale,
			height: GRID.height * scale,
		},
		// Three 106-wide cards with 8 between them, wrapping into 126-tall rows.
		gridContent: {
			flexDirection: 'row',
			flexWrap: 'wrap',
			width: (CARD_WIDTH * COLUMNS + GRID_GAP * (COLUMNS - 1)) * scale,
			gap: GRID_GAP * scale,
		},
		shelf: {
			position: 'absolute',
			left: CONTENT_X * scale,
			top: SHELF_TOP * scale,
		},
		status: {
			alignItems: 'center',
			justifyContent: 'center',
			gap: 16 * scale,
		},
		statusText: {
			color: achievementsColors.secondary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 12 * scale,
			fontWeight: '600',
			lineHeight: 12 * 1.4 * scale,
			textAlign: 'center',
		},
		retry: {
			paddingHorizontal: 24 * scale,
			paddingVertical: 10 * scale,
			borderRadius: RADIUS_PANEL * scale,
			borderWidth: Math.max(1, 1 * scale),
			borderColor: achievementsColors.gold50,
			backgroundColor: achievementsColors.well,
			...achievementsGlow.gold,
		},
		retryLabel: {
			color: achievementsColors.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: 14 * scale,
			fontWeight: '900',
		},
		menu: {
			position: 'absolute',
			left: (CONTENT_X + MENU.right - DROPDOWN_WIDTH) * scale,
			top: MENU.top * scale,
			zIndex: 2,
		},
	});

export default AchievementsScreen;
